import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Card, CardHeader } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Select } from '../components/common/Select';
import { useAuth } from '../context/AuthContext';
import { leaveService } from '../services/leaveService';
import { LeaveBalance, LeaveRequest, LeaveType, LeaveStatus } from '../types';

const leaveTypeOptions = [
  { value: 'CL', label: 'Casual Leave (CL)' },
  { value: 'PL', label: 'Period Leave (PL)' },
  { value: 'UNPAID', label: 'Unpaid Leave' },
];

const statusBadgeColors: Record<LeaveStatus, string> = {
  PENDING: 'bg-warning-50 text-warning-600',
  APPROVED: 'bg-success-50 text-success-600',
  REJECTED: 'bg-danger-50 text-danger-600',
  CANCELLED: 'bg-gray-100 text-gray-600',
};

export function LeavePage() {
  const { user, isHR } = useAuth();
  const [balance, setBalance] = useState<LeaveBalance | null>(null);
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [allRequests, setAllRequests] = useState<LeaveRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showApplyForm, setShowApplyForm] = useState(false);
  
  // Rejection modal state
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingRequestId, setRejectingRequestId] = useState<number | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Form state
  const [leaveType, setLeaveType] = useState<LeaveType>('CL');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isHalfDay, setIsHalfDay] = useState(false);
  const [reason, setReason] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [balanceData, myRequests] = await Promise.all([
        leaveService.getMyLeaveBalance(),
        leaveService.getMyLeaveRequests(),
      ]);
      setBalance(balanceData);
      setRequests(myRequests);

      if (isHR) {
        const all = await leaveService.getAllLeaveRequests('PENDING');
        setAllRequests(all);
      }
    } catch (error) {
      console.error('Error fetching leave data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyLeave = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate: Period Leave cannot be backdated by employees
    if (leaveType === 'PL' && !isHR) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDate = new Date(startDate);
      selectedDate.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        toast.error('Period Leave (PL) cannot be applied for past dates. Please contact HR/Admin for backdated PL requests.');
        return;
      }
    }
    
    setIsSubmitting(true);
    try {
      await leaveService.applyLeave({
        leaveType,
        startDate,
        endDate,
        isHalfDay,
        reason,
      });
      toast.success('Leave request submitted!');
      setShowApplyForm(false);
      resetForm();
      fetchData();
    } catch (error) {
      // Error handled by interceptor
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApprove = async (id: number, status: 'APPROVED' | 'REJECTED') => {
    if (status === 'REJECTED') {
      // Show rejection modal
      setRejectingRequestId(id);
      setShowRejectModal(true);
      return;
    }
    
    // Approve directly
    try {
      await leaveService.approveLeaveRequest(id, status);
      toast.success(`Leave request ${status.toLowerCase()}!`);
      fetchData();
    } catch (error) {
      // Error handled by interceptor
    }
  };

  const handleConfirmReject = async () => {
    if (!rejectingRequestId) return;
    
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    try {
      await leaveService.approveLeaveRequest(rejectingRequestId, 'REJECTED', rejectionReason);
      toast.success('Leave request rejected!');
      setShowRejectModal(false);
      setRejectingRequestId(null);
      setRejectionReason('');
      fetchData();
    } catch (error) {
      // Error handled by interceptor
    }
  };

  const handleCancelReject = () => {
    setShowRejectModal(false);
    setRejectingRequestId(null);
    setRejectionReason('');
  };

  const handleCancel = async (id: number) => {
    try {
      await leaveService.cancelLeaveRequest(id);
      toast.success('Leave request cancelled!');
      fetchData();
    } catch (error) {
      // Error handled by interceptor
    }
  };

  const resetForm = () => {
    setLeaveType('CL');
    setStartDate('');
    setEndDate('');
    setIsHalfDay(false);
    setReason('');
  };

  // Filter PL option for male employees
  const filteredLeaveTypeOptions = leaveTypeOptions.filter(
    (opt) => opt.value !== 'PL' || user?.employee?.firstName === 'Priya' // Simplified check
  );

  return (
    <div className="space-y-6 animate-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leave Management</h1>
          <p className="text-gray-500">Apply and track your leave requests</p>
        </div>
        <Button onClick={() => setShowApplyForm(!showApplyForm)}>
          {showApplyForm ? 'Cancel' : 'Apply Leave'}
        </Button>
      </div>

      {/* Apply Leave Form */}
      {showApplyForm && (
        <Card>
          <CardHeader title="Apply for Leave" />
          <form onSubmit={handleApplyLeave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Leave Type"
                options={filteredLeaveTypeOptions}
                value={leaveType}
                onChange={(e) => setLeaveType(e.target.value as LeaveType)}
                required
              />
              <div className="flex items-center gap-2 pt-8">
                <input
                  type="checkbox"
                  id="halfDay"
                  checked={isHalfDay}
                  onChange={(e) => setIsHalfDay(e.target.checked)}
                  className="w-4 h-4 text-primary-600 rounded"
                />
                <label htmlFor="halfDay" className="text-sm text-gray-700">
                  Half Day Leave
                </label>
              </div>
              <div>
                <Input
                  type="date"
                  label="Start Date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  min={leaveType === 'PL' && !isHR ? new Date().toISOString().split('T')[0] : undefined}
                  required
                />
                {leaveType === 'PL' && !isHR && (
                  <p className="text-xs text-orange-600 mt-1">
                    ⚠️ Period Leave cannot be applied for past dates
                  </p>
                )}
              </div>
              <Input
                type="date"
                label="End Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate || (leaveType === 'PL' && !isHR ? new Date().toISOString().split('T')[0] : undefined)}
                required
              />
            </div>
            <Input
              label="Reason"
              placeholder="Enter reason for leave"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <Button type="submit" isLoading={isSubmitting}>
              Submit Request
            </Button>
          </form>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Leave Balance */}
        <Card>
          <CardHeader title="Leave Balance" />
          {isLoading ? (
            <div className="flex justify-center py-4">
              <div className="spinner" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-primary-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-primary-700">Casual Leave</span>
                  <span className="text-2xl font-bold text-primary-600">
                    {balance?.casualLeave.balance || 0}
                  </span>
                </div>
                <p className="text-sm text-primary-600 mt-1">
                  Used: {balance?.casualLeave.used || 0} / Entitled: {balance?.casualLeave.entitled || 0}
                </p>
              </div>
              {balance && balance.paidLeave.entitled > 0 && (
                <div className="p-4 bg-success-50 rounded-lg border border-success-200">
                  <div className="flex justify-between items-center">
                    <span className="text-success-700">Period Leave</span>
                    <span className="text-2xl font-bold text-success-600">
                      {balance.paidLeave.balance}
                    </span>
                  </div>
                  <p className="text-sm text-success-600 mt-1">
                    Used: {balance.paidLeave.used} / Entitled: {balance.paidLeave.entitled}
                  </p>
                  <p className="text-xs text-orange-600 mt-2 font-medium">
                    ⚠️ Period Leave cannot be carried forward - unused PL lapses at month end
                  </p>
                </div>
              )}
              {balance && balance.unpaidUsed > 0 && (
                <div className="p-4 bg-danger-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-danger-700">Unpaid Leave Used</span>
                    <span className="text-2xl font-bold text-danger-600">
                      {balance.unpaidUsed}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* My Requests */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader title="My Leave Requests" />
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="spinner" />
              </div>
            ) : requests.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No leave requests found.
              </div>
            ) : (
              <div className="space-y-3">
                {requests.map((request) => (
                  <div
                    key={request.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{request.leaveType}</span>
                        <span className={`badge ${statusBadgeColors[request.status]}`}>
                          {request.status}
                        </span>
                        {request.isHalfDay && (
                          <span className="badge bg-gray-100 text-gray-600">Half Day</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {new Date(request.startDate).toLocaleDateString()} - {new Date(request.endDate).toLocaleDateString()}
                      </p>
                      {request.reason && (
                        <p className="text-sm text-gray-600 mt-1">{request.reason}</p>
                      )}
                      {request.status === 'REJECTED' && request.rejectedReason && (
                        <p className="text-sm text-danger-600 mt-1 font-medium">
                          Rejection Reason: {request.rejectedReason}
                        </p>
                      )}
                    </div>
                    {request.status === 'PENDING' && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleCancel(request.id)}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* HR: Pending Requests */}
      {isHR && allRequests.length > 0 && (
        <Card>
          <CardHeader title="Pending Leave Requests (HR)" />
          <div className="space-y-3">
            {allRequests.map((request) => (
              <div
                key={request.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">
                      {request.employee?.firstName} {request.employee?.lastName}
                    </span>
                    <span className="text-gray-500">({request.employee?.employeeCode})</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    {request.leaveType} • {new Date(request.startDate).toLocaleDateString()} - {new Date(request.endDate).toLocaleDateString()}
                    {request.isHalfDay && ' (Half Day)'}
                  </p>
                  {request.reason && (
                    <p className="text-sm text-gray-500 mt-1">Reason: {request.reason}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => handleApprove(request.id, 'APPROVED')}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleApprove(request.id, 'REJECTED')}
                  >
                    Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Rejection Reason Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader title="Reject Leave Request" />
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rejection Reason <span className="text-danger-600">*</span>
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Please provide a reason for rejecting this leave request..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  rows={4}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  This reason will be visible to the employee.
                </p>
              </div>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={handleCancelReject}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={handleConfirmReject}
                  disabled={!rejectionReason.trim()}
                >
                  Confirm Rejection
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
